import React, { useState } from 'react';

import { useField } from 'formik';
import classNames from 'classnames';
import { Form, Badge, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { AsYouType } from 'libphonenumber-js';

import { EyeOffOutlineIcon, EyeOutlineIcon } from 'assets/icons';
import { getMaxBirthday, getCurrentDate } from 'utils/Date';
import regex from 'constants/regex';

/**
 * Everything `Form.Control` takes, plus the field's Formik `name` and this component's own extras.
 * `type` widens to `string` because `'phone'` is ours, not a native input type.
 */
export interface IInputProps
  extends Omit<React.ComponentPropsWithoutRef<typeof Form.Control>, 'type'> {
  name: string;
  type?: string;
  label?: React.ReactNode;
  /** Emoji are stripped from the value unless this is set. */
  useEmoji?: boolean;
  /** Rendered inside the field, on the left. */
  icon?: React.ReactNode;
  /** Adds the show/hide toggle for a password field. */
  reveal?: boolean;
  /** Accessible name for that toggle. */
  revealLabel?: string;
  /** Rendered opposite the label, e.g. a "forgot password" link. */
  labelAction?: React.ReactNode;
  /** Tooltip content behind an `i` badge next to the label. */
  info?: React.ReactNode;
  'data-test'?: string;
}

/**
 * `icon` renders inside the field on the left and `reveal` adds a show/hide toggle for a password —
 * both opt-in, so every existing call site is untouched. The toggle swaps the control's `type`
 * rather than the value, so Formik never sees the password change.
 */
const Input = ({
  label,
  useEmoji = false,
  icon,
  reveal = false,
  revealLabel,
  labelAction,
  ...props
}: IInputProps) => {
  const [field, meta, helpers] = useField(props);
  const invalid = meta.touched && meta.error;
  const { setValue } = helpers;
  const asYouType = new AsYouType('US');
  const [revealed, setRevealed] = useState(false);

  field.onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newString: string = e.target.value;
    if (!useEmoji) {
      // https://ihateregex.io/expr/emoji/
      newString = newString.replace(regex.emoji, '');
    }
    setValue(newString);
  };

  if (props.type === 'phone') {
    field.onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(asYouType.input(e.target.value));
    };
  }

  if (props.type === 'date') {
    props.min = getMaxBirthday();
    if (props.max !== 'none') {
      props.max = getCurrentDate();
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { value } = e.target as HTMLInputElement;
    if (e.key === 'Backspace' && value.charAt(value.length - 1) === ')') {
      e.preventDefault();
      const rightBracketLessNumber = value.slice(0, value.length - 2);
      setValue(rightBracketLessNumber);
    }
  };

  /**
   * `props` is spread after `field`, so a caller's `onBlur` used to replace Formik's outright and
   * the field never became `touched` — which silently hid its own validation message, since
   * `invalid` is `touched && error`. Both run now, Formik's first.
   */
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(event);
    (props.onBlur as React.FocusEventHandler<HTMLInputElement> | undefined)?.(event);
  };

  const control = (
    <Form.Control
      // `isInvalid` is what adds `is-invalid`; a className doing the same put it in twice.
      isInvalid={!!invalid}
      onKeyDown={props.type === 'phone' ? handleKeyDown : undefined}
      {...field}
      {...props}
      onBlur={handleBlur}
      type={reveal && revealed ? 'text' : props.type}
    />
  );

  return (
    <>
      {label && (
        <div
          className={classNames({
            'd-flex justify-content-between align-items-center': labelAction
          })}
        >
          <Form.Label>
            {label} {props.required && '*'}{' '}
            {props.info && (
              <OverlayTrigger overlay={<Tooltip id="tooltip-info">{props.info}</Tooltip>}>
                <Badge pill bg="primary">
                  i
                </Badge>
              </OverlayTrigger>
            )}
          </Form.Label>
          {labelAction}
        </div>
      )}

      {icon || reveal ? (
        <div className={classNames('login-field', { 'has-reveal': reveal })}>
          {icon}
          {control}
          {reveal && (
            <button
              type="button"
              className="login-reveal"
              onClick={() => setRevealed(!revealed)}
              aria-label={revealLabel}
              data-test={`${props['data-test'] || 'input'}-reveal`}
            >
              {revealed ? (
                <EyeOffOutlineIcon className="login-reveal-icon" />
              ) : (
                <EyeOutlineIcon className="login-reveal-icon" />
              )}
            </button>
          )}
        </div>
      ) : (
        control
      )}
      {invalid && <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>}
    </>
  );
};

export default Input;
